// Export hook for the collaborator's own shift change requests listing (ASIS-5555).
// Wraps the generic useExport hook with the current listing filters. Excel-only,
// so a single "Descargar" button drives it — onExport takes no format argument.

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { shiftService } from '@/api/domains/shift';
import type { LC_ExportEmployeeShiftRequestsIn } from '@/api/domains/shift/shift.types';
import { useExport } from '@/hooks/ui/useExport';


// ── Params & result types ──────────────────────────────────────────────────
export interface UseShiftChangeRequestsEmployeeExportParams {
  /** Empleado autenticado. Cuando es null la descarga no se ejecuta. */
  employeeId: number | null;
  filters?: LC_ExportEmployeeShiftRequestsIn;
}

export interface UseShiftChangeRequestsEmployeeExportResult {
  onExport: () => Promise<void>;
  isExporting: boolean;
}

// ── Hook ───────────────────────────────────────────────────────────────────
export function useShiftChangeRequestsEmployeeExport(
  params: UseShiftChangeRequestsEmployeeExportParams,
): UseShiftChangeRequestsEmployeeExportResult {
  const { t: translate } = useTranslation();
  const { employeeId, filters = {} } = params;

  const { onExport: exportFile, isExporting } = useExport({
    exportFn: () => shiftService.exportEmployeeShiftRequests(employeeId as number, filters),
    successMessage: translate('attendance:shiftChange.messages.exportSuccess'),
    errorMessage: translate('attendance:shiftChange.messages.exportError'),
  });

  const onExport = useCallback(async () => {
    if (employeeId === null) return;
    await exportFile();
  }, [employeeId, exportFile]);

  return { onExport, isExporting };
}

export default useShiftChangeRequestsEmployeeExport;
